import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import type { NavItem } from "@/lib/navigation";

export type NavItemInput = {
  id?: string;
  area: "header" | "footer";
  column_label?: string | null;
  label: string;
  href: string;
  sort_order: number;
  is_visible: boolean;
  open_in_new: boolean;
};

/** Public: visible menu links for the header and footer. */
export const listNavItems = createServerFn({ method: "GET" }).handler(async (): Promise<NavItem[]> => {
  const sb = createClient<Database>(process.env["SUPABASE_URL"]!, process.env["SUPABASE_PUBLISHABLE_KEY"]!, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
  const { data } = await sb
    .from("nav_items")
    .select("id, area, column_label, label, href, sort_order, is_visible, open_in_new")
    .eq("is_visible", true)
    .order("area")
    .order("sort_order");
  return (data ?? []) as NavItem[];
});

/** Admin: every menu link, including hidden ones. */
export const listNavItemsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<NavItem[]> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { data, error } = await context.supabase
      .from("nav_items")
      .select("id, area, column_label, label, href, sort_order, is_visible, open_in_new")
      .order("area")
      .order("sort_order");
    if (error) throw new Error(error.message);
    return (data ?? []) as NavItem[];
  });

/**
 * Admin: replace the whole menu in one shot. Rows missing from the payload are
 * deleted, existing rows are updated, and new rows are inserted — so the editor
 * can save adds, edits, reorders and removals atomically enough for a menu.
 */
export const saveNavItems = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { items: NavItemInput[] }) => input)
  .handler(async ({ data, context }): Promise<{ saved: number }> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const items = data.items
      .filter((i) => i.label.trim() && i.href.trim())
      .map((i, index) => ({
        id: i.id,
        area: i.area,
        column_label: i.area === "footer" ? (i.column_label?.trim() || "More") : null,
        label: i.label.trim(),
        href: i.href.trim(),
        sort_order: Number.isFinite(i.sort_order) ? i.sort_order : index * 10,
        is_visible: i.is_visible,
        open_in_new: i.open_in_new,
      }));

    const keepIds = items.map((i) => i.id).filter((id): id is string => !!id);
    const { data: existing, error: exErr } = await context.supabase.from("nav_items").select("id");
    if (exErr) throw new Error(exErr.message);
    const toDelete = (existing ?? []).map((r) => r.id).filter((id) => !keepIds.includes(id));
    if (toDelete.length) {
      const { error } = await context.supabase.from("nav_items").delete().in("id", toDelete);
      if (error) throw new Error(error.message);
    }

    for (const item of items) {
      if (item.id) {
        const { id, ...rest } = item;
        const { error } = await context.supabase.from("nav_items").update(rest).eq("id", id);
        if (error) throw new Error(error.message);
      } else {
        const { id: _omit, ...rest } = item;
        const { error } = await context.supabase.from("nav_items").insert(rest);
        if (error) throw new Error(error.message);
      }
    }

    return { saved: items.length };
  });

export function navQueryOptions() {
  return queryOptions({
    queryKey: ["nav_items"],
    queryFn: () => listNavItems(),
    staleTime: 60_000,
  });
}
